import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '@/store';
import { cn } from '@/utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Waves,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserProfile } from '@/contexts/AuthContext';
import UserProfileButton from '../shared/UserProfileButton';

// Simple Input component since we don't have one in the UI components
const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { className?: string }
>(({ className, ...props }, ref) => {
  return (
    <input
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Input.displayName = "Input";

interface MainHeaderProps {
  showLoginInHeader?: boolean;
  initiateGoogleLogin?: () => void;
  userProfile?: UserProfile | null;
  handleLogout?: () => void;
  onSearchFocusChange?: (isFocused: boolean) => void;
  onSearchQueryChange?: (query: string) => void;
}

export default function MainHeader({
  showLoginInHeader = false,
  initiateGoogleLogin,
  userProfile,
  handleLogout,
  onSearchFocusChange,
  onSearchQueryChange
}: MainHeaderProps) {
  const { selectedTab, setSelectedTab, setSelectedStreamIcon, setSelectedStudioIcon } = useStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchFormRef = useRef<HTMLFormElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle tab selection
  const handleTabSelect = (tab: 'stream' | 'studio') => {
    setSelectedTab(tab);
    navigate(`/${tab}`);

    // Reset icon selection when changing tabs
    if (tab === 'stream') {
      setSelectedStreamIcon('news');
    } else {
      setSelectedStudioIcon('alerts');
    }
  };

  // Sync tab selection with URL on initial load and URL changes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/studio')) {
      setSelectedTab('studio');
    } else if (path.includes('/stream')) {
      setSelectedTab('stream');
    }
  }, [location.pathname, setSelectedTab]);

  // Check for mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle clicks outside the search form
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchFormRef.current && 
        !searchFormRef.current.contains(event.target as Node) && 
        isSearchFocused
      ) {
        setIsSearchFocused(false);
        if (onSearchFocusChange) {
          onSearchFocusChange(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchFocused, onSearchFocusChange]);

  // Handle keyboard events for the search input
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSearchFocused) {
        setIsSearchFocused(false);
        searchInputRef.current?.blur();
        if (onSearchFocusChange) {
          onSearchFocusChange(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchFocused, onSearchFocusChange]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    if (onSearchQueryChange) {
      onSearchQueryChange(newQuery);
    }
  };

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Implement search functionality here
    console.log('Search query:', searchQuery);
  };

  // Handle search focus
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
    if (onSearchFocusChange) {
      onSearchFocusChange(true);
    }
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchQuery('');
    if (onSearchQueryChange) {
      onSearchQueryChange('');
    }
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Determine if we should show the login button in this header
  const shouldShowLoginButton = isMobile || showLoginInHeader;

  return (
    <header className="border-b">
      <div className="flex items-center justify-between px-4 h-11">
        {/* Left section: Logo and Tabs */}
        <div className="flex items-center">
          {/* Logo and Brand - Always visible */}
          <div className="flex items-center mr-8">
            {/* Use the SVG favicon */}
            <img src="/favicon.svg" alt="Lucid Logo" className="w-5 h-5 mr-2" />
            <span className="logo-font font-semibold text-lg">
              Lucid
            </span>
          </div>

          {/* Tab Navigation - Hidden when search is focused */}
          <div className={cn(
            "flex h-full transition-all duration-300",
            isSearchFocused ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
          )}>
            {/* Stream Tab - Always visible */}
            <Button
              variant="ghost"
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-none border-b-2 h-full",
                selectedTab === 'stream'
                  ? "border-blue-500 text-blue-500"
                  : "border-transparent hover:border-gray-300"
              )}
              onClick={() => handleTabSelect('stream')}
            >
              <Waves className="h-4 w-4" />
              <span>Stream</span>
            </Button>

            {/* Studio Tab - Hidden on mobile if showLoginInHeader is true */}
            {(!isMobile || !showLoginInHeader) && (
              <Button
                variant="ghost"
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-none border-b-2 h-full",
                  selectedTab === 'studio'
                    ? "border-blue-500 text-blue-500"
                    : "border-transparent hover:border-gray-300"
                )}
                onClick={() => handleTabSelect('studio')}
              >
                <Home className="h-4 w-4" />
                <span>Studio</span>
              </Button>
            )}
          </div>
        </div>

        {/* Middle section: Search Bar - Hidden on mobile if showLoginInHeader is true */}
        {(!isMobile || !showLoginInHeader) && (
          <form 
            ref={searchFormRef}
            onSubmit={handleSearchSubmit} 
            className={cn(
              "transition-all duration-300 ease-in-out",
              isSearchFocused 
                ? "flex-1 max-w-[calc(100%-200px)] mx-4 z-10" 
                : "flex-1 max-w-md mx-4"
            )}
          >
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                className={cn(
                  "pl-8 w-full",
                  isSearchFocused && searchQuery ? "pr-8" : ""
                )}
              />
              {isSearchFocused && searchQuery && (
                <button
                  type="button"
                  onClick={handleSearchClear}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </form>
        )}

        {/* Right section: Login Button or User Info - Only shown on mobile or if explicitly requested */}
        {shouldShowLoginButton && (
          <div className="flex h-full items-center">
            <UserProfileButton 
              userProfile={userProfile || null}
              handleLogout={handleLogout}
              initiateGoogleLogin={initiateGoogleLogin}
              buttonStyle="circle"
            />
          </div>
        )}
      </div>
    </header>
  );
} 